import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import * as XLSX from 'xlsx';
import { Download, Search, ChevronLeft, ChevronRight } from 'lucide-react';

const Logs = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filteredLogs, setFilteredLogs] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const logsPerPage = 20;
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFilter, setDateFilter] = useState({
        startDate: '',
        endDate: ''
    });
    const [actionFilter, setActionFilter] = useState('');
    const [uniqueActions, setUniqueActions] = useState([]);
    const { user } = useAuth();

    

    useEffect(() => {
        fetchLogs();
    }, []);

    // Extraer acciones únicas para el filtro
    useEffect(() => {
        if (logs.length > 0) {
            const actions = [...new Set(logs.map(log => log.accion))];
            setUniqueActions(actions);
        }
    }, [logs]);

    const fetchLogs = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/db/logs', {
                headers: {
                    Authorization: `Bearer ${token}`
                },
                cache: 'no-store'
            });

            const data = await response.json();
            if (data.success) {
                setLogs(data.data);
                setFilteredLogs(data.data);
            }
        } catch (error) {
            console.error('Error al obtener logs:', error);
        } finally {
            setLoading(false);
        }
    };

    // Función para filtrar logs
    useEffect(() => {
        let result = logs;

        // Filtrar por término de búsqueda
        if (searchTerm) {
            result = result.filter(log =>
                log.usuario.toLowerCase().includes(searchTerm.toLowerCase()) ||
                log.organizacion.toLowerCase().includes(searchTerm.toLowerCase()) ||
                log.accion.toLowerCase().includes(searchTerm.toLowerCase()) ||
                log.descripcion.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Filtrar por fecha
        if (dateFilter.startDate) {
            result = result.filter(log =>
                new Date(log.fecha) >= new Date(dateFilter.startDate)
            );
        }
        if (dateFilter.endDate) {
            result = result.filter(log =>
                new Date(log.fecha) <= new Date(dateFilter.endDate + 'T23:59:59')
            );
        }

        // Filtrar por acción
        if (actionFilter) {
            result = result.filter(log => log.accion === actionFilter);
        }

        setFilteredLogs(result);
        setCurrentPage(1);
    }, [searchTerm, dateFilter, actionFilter, logs]);

    // Funciones de paginación
    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const exportToExcel = () => {
        // Log the export action first
        logExportAction();

        // Existing Excel export code...
        const dataToExport = currentLogs.map(log => ({
            Fecha: new Date(log.fecha).toLocaleString(),
            Usuario: log.usuario,
            Organización: log.organizacion,
            Acción: log.accion,
            Descripción: log.descripcion
        }));

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Logs");

        const fileName = `logs_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };

    // Add this function to log the export action
    const logExportAction = async () => {
        try {
            const token = localStorage.getItem('token');

            let descripcion = `Exportó un reporte Excel con ${filteredLogs.length} registros de actividad`;
            if (searchTerm) descripcion += `, filtrado por: "${searchTerm}"`;
            if (dateFilter.startDate) descripcion += `, desde: ${dateFilter.startDate}`;
            if (dateFilter.endDate) descripcion += `, hasta: ${dateFilter.endDate}`;
            if (actionFilter) descripcion += `, acción: "${actionFilter}"`;

            await fetch('/db/log-export', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    accion: 'Exportar Reporte',
                    descripcion: descripcion
                }),
                cache: 'no-store'
            });
        } catch (error) {
            console.error('Error al registrar la exportación del reporte:', error);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <span className="loading loading-spinner loading-lg"></span>
            </div>
        );
    }

    const indexOfLastLog = currentPage * logsPerPage;
    const indexOfFirstLog = indexOfLastLog - logsPerPage;
    const currentLogs = filteredLogs.slice(indexOfFirstLog, indexOfLastLog);
    const totalPages = Math.ceil(filteredLogs.length / logsPerPage);

    return (
        <div className="container mx-auto p-4">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">Registro de Actividades</h1>
                <button
                    onClick={exportToExcel}
                    className="btn btn-primary"
                >
                    <Download className="w-4 h-4 mr-2" />
                    Exportar a Excel
                </button>
            </div>

            {/* Filtros */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="form-control">
                    <label className="label">
                        <span className="label-text">Buscar</span>
                    </label>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="w-4 h-4 text-gray-500" />
                        </div>
                        <input
                            type="text"
                            placeholder="Buscar..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
                <div className="form-control">
                    <label className="label">
                        <span className="label-text">Fecha inicial</span>
                    </label>
                    <input
                        type="date"
                        className="input input-bordered"
                        value={dateFilter.startDate}
                        onChange={(e) => setDateFilter({ ...dateFilter, startDate: e.target.value })}
                    />
                </div>
                <div className="form-control">
                    <label className="label">
                        <span className="label-text">Fecha final</span>
                    </label>
                    <input
                        type="date"
                        className="input input-bordered"
                        value={dateFilter.endDate}
                        onChange={(e) => setDateFilter({ ...dateFilter, endDate: e.target.value })}
                    />
                </div>
                <div className="form-control">
                    <label className="label">
                        <span className="label-text">Acción</span>
                    </label>
                    <select
                        className="select select-bordered w-full"
                        value={actionFilter}
                        onChange={(e) => setActionFilter(e.target.value)}
                    >
                        <option value="">Todas las acciones</option>
                        {uniqueActions.map((action, index) => (
                            <option key={index} value={action}>
                                {action}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Tabla de logs */}
            <div className="overflow-x-auto bg-base-100 rounded-lg shadow">
                <table className="table table-zebra w-full">
                    <thead>
                        <tr>
                            <th>Fecha</th>
                            <th>Usuario</th>
                            <th>Organización</th>
                            <th>Acción</th>
                            <th>Descripción</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentLogs.map((log) => (
                            <tr key={log._id}>
                                <td className="whitespace-nowrap">
                                    {new Date(log.fecha).toLocaleString()}
                                </td>
                                <td>{log.usuario}</td>
                                <td>{log.organizacion}</td>
                                <td>{log.accion}</td>
                                <td>{log.descripcion}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {filteredLogs.length === 0 && (
                <div className="text-center py-4">
                    <p className="text-gray-500">No hay registros disponibles</p>
                </div>
            )}

            {/* Paginación */}
            <div className="flex justify-between items-center mt-4">
                <div className="text-sm text-gray-500">
                    Total de registros: {filteredLogs.length}
                </div>
                
                <div className="flex items-center space-x-2">
                    <button 
                        className="btn btn-sm btn-outline" 
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Anterior
                    </button>
                    
                    <span className="px-2">
                        Página {currentPage} de {totalPages || 1}
                    </span>
                    
                    <button 
                        className="btn btn-sm btn-outline" 
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages || totalPages === 0}
                    >
                        Siguiente
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Logs;